import {
    BoxedRow,
    Callout,
    HorizontalScroll,
    IconAlertRegular,
    IconWarningRegular,
    PageBullets,
    skinVars,
    useWindowSize,
} from '@telefonica/mistica';
import { formatMobileNumber } from '../../utils/dataTransformation/unitsConversion';
import './index.scss';
import { InfoSIM } from '../../models/ConfigureLine';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useConfigureLineContext } from '../../utils/store/context/configure-line-context/ConfigureLineContext';

type WarningLineType = {
    warningsInfo: InfoSIM[];
    blockedLine?: {
        blocked: boolean;
        number: string;
        productId: string;
    };
};

export const WarningLine = ({ warningsInfo, blockedLine }: WarningLineType) => {
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [groupedRows, setGroupedRows] = useState<InfoSIM[][]>([]);
    const size = useWindowSize();
    const isConvergent: boolean = true;
    const { setLineActive } = useConfigureLineContext();

    useEffect(() => {
        const tempArray = [];
        const filteredWarnings =
            warningsInfo?.filter(
                (simInfo) => simInfo.pending || simInfo.multiSims?.some((multiSim) => multiSim.pending),
            ) ?? [];

        if (size.width >= 1024 && filteredWarnings.length) {
            for (let i = 0; i < filteredWarnings?.length; i += 3) {
                tempArray.push(filteredWarnings.slice(i, i + 3));
            }
        }

        setGroupedRows(tempArray);
    }, [size.width, warningsInfo]);

    const getWarningTitle = (simInfo: InfoSIM) => {
        if (simInfo.multiSims && simInfo.multiSims.some((multiSim) => multiSim.pending))
            return 'multiSimActivationWarning';
        else {
            if (simInfo.type === 'E_SIM') return 'eSimInstallWarning';
            else return 'simActiveWarning';
        }
    };

    if (!Array.isArray(warningsInfo)) {
        return (
            <div className='warningEsim-component'>
                {isVisible && (
                    <div className='warningEsim-component__callout'>
                        <Callout
                            title='Ha fallado en la carga de información.'
                            description='Lo sentimos, en este momento no podemos mostrarte esta información de recordatorio. Vuelve a intentarlo más tarde.'
                            icon={
                                <IconWarningRegular color={skinVars.colors.error} size={size.width >= 1024 ? 24 : 16} />
                            }
                            onClose={() => {
                                setIsVisible(false);
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }

    const handleChange = (index: number) => {
        setCurrentIndex(index);
    };

    return (
        <div className='warningEsim-component'>
            <div className='warningEsim-component__convergent'>
                {size.width < 1024 ? (
                    <HorizontalScroll>
                        {warningsInfo?.map((simInfo, index) => {
                            if (simInfo.pending || simInfo.multiSims?.some((multiSim) => multiSim.pending))
                                return (
                                    <div className='warningEsim-component__convergent-boxedRow' key={index}>
                                        <BoxedRow
                                            onPress={() => {
                                                setLineActive(simInfo.productId);
                                                navigate(`/configure-line`, {
                                                    state: { isConvergent },
                                                });
                                            }}
                                            role='warning'
                                            title={t(getWarningTitle(simInfo))}
                                            description={formatMobileNumber(simInfo?.number)}
                                            asset={<IconAlertRegular size={24} color={skinVars.colors.warning} />}
                                            withChevron={true}
                                        />
                                    </div>
                                );
                        })}
                        {blockedLine?.blocked && (
                            <div className='warningEsim-component__convergent-boxedRow'>
                                <BoxedRow
                                    onPress={() => {
                                        setLineActive(blockedLine.productId);
                                        navigate(`/configure-line`, {
                                            state: { isConvergent },
                                        });
                                    }}
                                    role='warning'
                                    title={t('blockedLine')}
                                    description={formatMobileNumber(blockedLine.number)}
                                    asset={<IconAlertRegular size={24} color={skinVars.colors.warning} />}
                                    withChevron={true}
                                />
                            </div>
                        )}
                    </HorizontalScroll>
                ) : (
                    <>
                        <div className='warningEsim-component__convergent-wrapper'>
                            {groupedRows[currentIndex]?.map((simInfo, index) => {
                                if (simInfo.pending || simInfo.multiSims.some((multiSim) => multiSim.pending))
                                    return (
                                        <div className='warningEsim-component__convergent-boxedRow' key={index}>
                                            <BoxedRow
                                                onPress={() => {
                                                    setLineActive(simInfo.productId);
                                                    navigate(`/configure-line`, {
                                                        state: { isConvergent },
                                                    });
                                                }}
                                                role='warning'
                                                title={t(getWarningTitle(simInfo))}
                                                description={formatMobileNumber(simInfo?.number)}
                                                asset={<IconAlertRegular size={24} color={skinVars.colors.warning} />}
                                                withChevron={true}
                                            />
                                        </div>
                                    );
                            })}
                            {blockedLine?.blocked &&
                                (!groupedRows[currentIndex] || groupedRows[currentIndex]?.length < 3) && (
                                    <div className='warningEsim-component__convergent-boxedRow'>
                                        <BoxedRow
                                            onPress={() => {
                                                setLineActive(blockedLine?.productId);
                                                navigate(`/configure-line`, {
                                                    state: { isConvergent },
                                                });
                                            }}
                                            role='warning'
                                            title={t('blockedLine')}
                                            description={formatMobileNumber(blockedLine?.number)}
                                            asset={<IconAlertRegular size={24} color={skinVars.colors.warning} />}
                                            withChevron={true}
                                        />
                                    </div>
                                )}
                        </div>

                        {groupedRows.length >= 2 && (
                            <div className='warningEsim-component__bullets'>
                                <PageBullets
                                    numPages={groupedRows.length}
                                    currentIndex={currentIndex}
                                    onPress={handleChange}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
